import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../../core/services/api.service';
import { LookupService } from '../../../core/services/lookup.service';
import { Utils } from '../../../Utils/utils';
import { AccountModel } from '../../../shared/models/loginuser.model';
import { ApiEndPoints, ApiRoot } from '../../../shared/constants/api-endpoints';
import { LookupKey } from '../../../shared/constants/lookup-keys';
import { SharedService } from '../../../shared/services/shared.service';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';
import { InlineCalendarComponent } from '../../../shared/components/inline-calendar/inline-calendar.component';
import { SkeletonLoaderComponent } from '../../../shared/components/skeleton-loader/skeleton-loader.component';

interface StatusChartRow {
  serviceStatus: string;
  srCount: number;
  color: string;
  widthPercent: number;
}

@Component({
  selector: 'app-statics',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS, InlineCalendarComponent, SkeletonLoaderComponent],
  templateUrl: './statics.component.html',
  styleUrl: './statics.component.scss'
})
export class StaticsComponent {
  filterForm: FormGroup;
  lookupKey = LookupKey;
  userType = '';
  accountCSN = '';
  accountId = '';
  membershipAccounts: any[] = [];
  serviceOptions: any[] = [];
  statusOptions: any[] = [];

  noStatusCountData = false;
  noSpendingData = false;
  isStatusChartLoading = false;
  isSpendingChartLoading = false;

  statusCountRows: StatusChartRow[] = [];
  statusTotal = 0;
  selectedServiceLabel = '';

  statusDonutChartData: { data: Array<{ name: string; y: number; color?: string }> } = {
    data: []
  };
  spendingBarChartData: {
    title?: string | null;
    xAxisTitle?: string | null;
    yAxisTitle?: string | null;
    indexAxis?: 'x' | 'y';
    categories: string[];
    series: Array<{ name: string; color?: string; colors?: string[]; data: number[] }>;
    showYAxisCurrencyIcon?: boolean;
    yAxisCurrencyIconUrl?: string;
    yAxisCurrencyIconSize?: number;
    barCategoryPercentage?: number;
    barPercentage?: number;
    maxBarThickness?: number;
    minBarLength?: number;
    hideVerticalGridLines?: boolean;
    dashedHorizontalGridLines?: boolean;
    isRtl?: boolean;
  } = {
    title: '',
    xAxisTitle: '',
    yAxisTitle: '',
    indexAxis: 'x',
    categories: [],
    series: []
  };

  today: Date = new Date();
  showCustomDateRange = false;
  showDateFromCalendar = false;
  showDateToCalendar = false;
  selectedDateFrom: Date | null = null;
  selectedDateTo: Date | null = null;
  private statusOptionsLoaded = false;
  private serviceOptionsLoaded = false;
  private hasInitializedCharts = false;

  lang = 'en';

  private readonly serviceColors = [
    '#0E2E83',
    '#2449A6',
    '#3D7EDB',
    '#56A8EA',
    '#A9CCF9',
    '#D6E6FF'
  ];
  private statusInFlightKey: string | null = null;
  private spendingInFlightKey: string | null = null;
  private lastStatusCompletedKey = '';
  private lastSpendingCompletedKey = '';
  private lastStatusCompletedAt = 0;
  private lastSpendingCompletedAt = 0;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private sharedService: SharedService,
    private lookupService: LookupService,
    private translate: TranslateService
  ) {
    this.lang = this.sharedService.getCurrentLang();
    this.userType = this.sharedService.getPrimaryAccount()?.['Account Type Code'] ?? '';
    this.accountCSN = this.sharedService.getCsnAccountId('csn');
    this.accountId = this.sharedService.getCsnAccountId('id');

    this.filterForm = this.fb.group({
      membership: [null],
      reportPeriod: ['Last year'],
      serviceName: [null]
    });

    this.buildMembershipAccounts();
    this.filterForm.get('membership')?.setValue(this.accountCSN || this.accountId);

    this.loadStatusOptions();
    this.loadServiceOptions();
  }

  get dateFromDisplay(): string {
    return this.formatDateDisplay(this.selectedDateFrom);
  }

  get dateToDisplay(): string {
    return this.formatDateDisplay(this.selectedDateTo);
  }

  get statusTotalDisplay(): string {
    return this.formatCompactNumber(this.statusTotal);
  }

  onMembershipChange(option: any) {
    if (option?.selectedLov?.isCSN) {
      this.accountCSN = option.selectedLov?.Value || this.sharedService.getCsnAccountId('csn');
      this.accountId = '';
    } else {
      this.accountId = option?.selectedLov?.Value || this.sharedService.getCsnAccountId('id');
      this.accountCSN = '';
    }

    this.refreshCharts();
  }

  onServiceChange(option: any) {
    this.selectedServiceLabel = this.getLovLabel(option?.selectedLov) || '';
    this.refreshCharts();
  }

  onReportPeriodChange() {
    const period = this.filterForm.get('reportPeriod')?.value || 'Last year';
    if (period === 'Custom') {
      this.showCustomDateRange = true;
      return;
    }

    this.showCustomDateRange = false;
    this.selectedDateFrom = null;
    this.selectedDateTo = null;
    this.refreshCharts();
  }

  toggleDateCalendar(target: 'from' | 'to') {
    if (target === 'from') {
      this.showDateFromCalendar = !this.showDateFromCalendar;
      this.showDateToCalendar = false;
      return;
    }
    this.showDateToCalendar = !this.showDateToCalendar;
    this.showDateFromCalendar = false;
  }

  clearDateFrom(event?: MouseEvent) {
    event?.stopPropagation();
    this.selectedDateFrom = null;
    if (this.selectedDateTo) {
      this.selectedDateTo = null;
    }
    this.showDateFromCalendar = false;
    this.refreshCharts();
  }

  clearDateTo(event?: MouseEvent) {
    event?.stopPropagation();
    this.selectedDateTo = null;
    this.showDateToCalendar = false;
    this.refreshCharts();
  }

  onDateFromPicked(date: Date) {
    this.selectedDateFrom = date;
    this.showDateFromCalendar = false;
    if (this.selectedDateTo && this.selectedDateTo < date) {
      this.selectedDateTo = null;
      return;
    }
    this.refreshCharts();
  }

  onDateToPicked(date: Date) {
    this.selectedDateTo = date;
    this.showDateToCalendar = false;
    this.refreshCharts();
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(value || 0);
  }

  private refreshCharts() {
    this.loadStatusCountData();
    this.loadSpendingAmountData();
  }

  private buildMembershipAccounts() {
    const accounts = Utils.checkIsArray(this.sharedService.getUserProfile()?.Account) ?? [];
    const dcNameKey = this.lang === 'ar' ? 'DC Name Arabic' : 'DC Name English';

    this.membershipAccounts = accounts
      .filter((u: AccountModel) => u['DC Name English'])
      .map((u: AccountModel) => ({
        Name: u['CSN'] ? `${u[dcNameKey]} (${u['CSN']})` : `${u[dcNameKey]}`,
        Value: u['CSN'] ? u['CSN'] : u['Id'],
        isCSN: !!u['CSN']
      }));
  }

  private loadStatusOptions() {
    this.lookupService
      .post<any>(LookupKey.LOOKUP_URL, {
        ProcessName: 'DC Get List Of Values WF',
        Type: LookupKey.SR_STATUS,
        ParentValue: ''
      })
      .subscribe({
        next: (result: any) => {
          this.statusOptions = Utils.checkIsArray(result?.SiebelMessage?.['List Of Values']);
          this.statusOptionsLoaded = true;
          this.triggerInitialChartsLoad();
        },
        error: () => {
          this.statusOptions = [];
          this.statusOptionsLoaded = true;
          this.triggerInitialChartsLoad();
        }
      });
  }

  private loadServiceOptions() {
    this.lookupService
      .post<any>(LookupKey.LOOKUP_URL, {
        ProcessName: 'DC Get List Of Values WF',
        Type: LookupKey.DC_SR_TYPE,
        ParentValue: this.userType || ''
      })
      .subscribe({
        next: (result: any) => {
          this.serviceOptions = Utils.checkIsArray(result?.SiebelMessage?.['List Of Values']);
          this.serviceOptionsLoaded = true;
          this.triggerInitialChartsLoad();
        },
        error: () => {
          this.serviceOptions = [];
          this.serviceOptionsLoaded = true;
          this.triggerInitialChartsLoad();
        }
      });
  }

  private triggerInitialChartsLoad() {
    // Guard initial load: both LOVs return asynchronously; without this check,
    // each callback can trigger chart APIs and cause duplicate calls on page load.
    if (this.hasInitializedCharts) {
      return;
    }
    if (!this.statusOptionsLoaded || !this.serviceOptionsLoaded) {
      return;
    }
    this.hasInitializedCharts = true;
    this.refreshCharts();
  }

  private loadStatusCountData() {
    if (this.showCustomDateRange && !this.selectedDateFrom) {
      this.isStatusChartLoading = false;
      return;
    }

    const period = this.filterForm.get('reportPeriod')?.value || 'Last year';
    const dateRange = this.getRequestDateRange(period);
    const accountNumber = this.filterForm.get('membership')?.value || this.accountCSN || this.accountId || '';
    const srCatType = this.filterForm.get('serviceName')?.value || '';

    this.noStatusCountData = false;

    const payload = {
      account_number: accountNumber,
      sr_cat_type_cd: srCatType,
      login: '',
      Period: period !== 'Custom' ? period : '',
      From_Date: dateRange.fromDate,
      To_Date: dateRange.toDate
    };
    const requestKey = JSON.stringify(payload);
    // De-dupe guard: same payload can be triggered multiple times on init/render cycles.
    const isRecentDuplicate =
      this.lastStatusCompletedKey === requestKey &&
      (Date.now() - this.lastStatusCompletedAt) < 1500;
    if (this.statusInFlightKey === requestKey || isRecentDuplicate) {
      return;
    }
    this.statusInFlightKey = requestKey;
    this.isStatusChartLoading = true;

    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_STATUS_COUNT_REST, payload)
      .subscribe({
        next: (response: any) => {
          const records = Utils.checkIsArray(response?.serviceStatus);
          const data = records
            .map((row: any) => {
              const statusLabel = Utils.getStatusLabel(row?.serviceStatus, this.statusOptions);
              const srCount = Number(row?.srCount);
              return {
                name: statusLabel || row?.serviceStatus || '-',
                y: Number.isFinite(srCount) ? srCount : 0
              };
            })
            .filter((item: { name: string; y: number }) => !!item.name);

          this.noStatusCountData = data.length === 0;
          this.updateStatusVisuals(data);
          this.lastStatusCompletedKey = requestKey;
          this.lastStatusCompletedAt = Date.now();
          this.statusInFlightKey = null;
          this.isStatusChartLoading = false;
        },
        error: () => {
          this.noStatusCountData = true;
          this.updateStatusVisuals([]);
          this.statusInFlightKey = null;
          this.isStatusChartLoading = false;
        }
      });
  }

  private loadSpendingAmountData() {
    if (this.showCustomDateRange && !this.selectedDateFrom) {
      this.isSpendingChartLoading = false;
      return;
    }

    const period = this.filterForm.get('reportPeriod')?.value || 'Last year';
    const dateRange = this.getRequestDateRange(period);
    const accountNumber = this.filterForm.get('membership')?.value || this.accountCSN || this.accountId || '';
    const srCatType = this.filterForm.get('serviceName')?.value || '';

    this.noSpendingData = false;

    const payload = {
      account_number: accountNumber,
      sr_cat_type_cd: srCatType,
      login: '',
      Period: period !== 'Custom' ? period : '',
      From_Date: dateRange.fromDate,
      To_Date: dateRange.toDate
    };
    const requestKey = JSON.stringify(payload);
    // De-dupe guard: same payload can be triggered multiple times on init/render cycles.
    const isRecentDuplicate =
      this.lastSpendingCompletedKey === requestKey &&
      (Date.now() - this.lastSpendingCompletedAt) < 1500;
    if (this.spendingInFlightKey === requestKey || isRecentDuplicate) {
      return;
    }
    this.spendingInFlightKey = requestKey;
    this.isSpendingChartLoading = true;

    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_SPENDING_AMOUNT_REST, payload)
      .subscribe({
        next: (response: any) => {
          const records = this.getSpendingAmountRows(response);
          this.noSpendingData = records.length === 0;
          this.updateSpendingVisuals(records);
          this.lastSpendingCompletedKey = requestKey;
          this.lastSpendingCompletedAt = Date.now();
          this.spendingInFlightKey = null;
          this.isSpendingChartLoading = false;
        },
        error: () => {
          this.noSpendingData = true;
          this.updateSpendingVisuals([]);
          this.spendingInFlightKey = null;
          this.isSpendingChartLoading = false;
        }
      });
  }

  private updateStatusVisuals(data: Array<{ name: string; y: number }>) {
    const total = data.reduce((sum, item) => sum + (item.y || 0), 0);
    const max = Math.max(...data.map((item) => item.y || 0), 0);

    this.statusTotal = total;

    this.statusCountRows = data.map((item, index) => {
      const color = this.serviceColors[index % this.serviceColors.length];
      return {
        serviceStatus: item.name,
        srCount: item.y,
        color,
        widthPercent: max > 0 ? (item.y / max) * 100 : 0
      };
    });

    this.statusDonutChartData = {
      data: this.statusCountRows.map((row) => ({
        name: row.serviceStatus,
        y: row.srCount,
        color: row.color
      }))
    };
  }

  private updateSpendingVisuals(records: any[]) {
    const rows = records
      .map((row) => {
        const monthYear = this.getValueFromRow(row, ['monthYear', 'MonthYear', 'MONTHYEAR']);
        const spentAmountRaw = this.getValueFromRow(row, ['spentAmount', 'SpentAmount', 'SPENTAMOUNT']);
        const spentAmount = Number(spentAmountRaw);
        return monthYear
          ? { monthYear, spentAmount: Number.isFinite(spentAmount) ? spentAmount : 0 }
          : null;
      })
      .filter((item: { monthYear: string; spentAmount: number } | null) => !!item) as Array<{
        monthYear: string;
        spentAmount: number;
      }>;

    if (!rows.length) {
      this.spendingBarChartData = {
        title: '',
        xAxisTitle: '',
        yAxisTitle: '',
        indexAxis: 'x',
        categories: [],
        series: [],
        showYAxisCurrencyIcon: true,
        yAxisCurrencyIconUrl: '/images/aed-currency.png',
        yAxisCurrencyIconSize: 24,
        barCategoryPercentage: 1,
        barPercentage: 0.6,
        maxBarThickness: 30,
        minBarLength: 5,
        hideVerticalGridLines: true,
        dashedHorizontalGridLines: true,
        isRtl: this.sharedService.getCurrentLang() === 'ar'
      };
      return;
    }

    const maxAmount = Math.max(...rows.map((row) => row.spentAmount), 0);
    const highlightIndex = rows.findIndex((row) => row.spentAmount === maxAmount);
    const colors = rows.map((_, index) => index === highlightIndex ? '#0E2E83' : '#3D89D8');

    this.spendingBarChartData = {
      title: '',
      xAxisTitle: '',
      yAxisTitle: '',
      indexAxis: 'x',
      categories: rows.map((row) => this.getShortMonthLabel(row.monthYear)),
      series: [
        {
          name: this.translate.instant('STATICS.SPENDING_CHART'),
          colors,
          data: rows.map((row) => row.spentAmount)
        }
      ],
      showYAxisCurrencyIcon: true,
      yAxisCurrencyIconUrl: '/images/aed-currency.png',
      yAxisCurrencyIconSize: 24,
      barCategoryPercentage: 1,
      barPercentage: 0.6,
      maxBarThickness: 30,
      minBarLength: 5,
      hideVerticalGridLines: true,
      dashedHorizontalGridLines: true,
      isRtl: this.sharedService.getCurrentLang() === 'ar'
    };
  }

  private getSpendingAmountRows(response: any): any[] {
    const candidates = [response?.spendingAmount];
    for (const candidate of candidates) {
      const list = Utils.checkIsArray(candidate);
      if (list.length) {
        return list;
      }
    }
    return [];
  }

  private getShortMonthLabel(monthYear: string): string {
    const token = String(monthYear || '').trim();
    if (!token) {
      return '-';
    }

    if (token.length <= 3) {
      return token.toUpperCase();
    }

    const firstPart = token.split(/[-/\s]/).find((part) => !!part);
    if (firstPart) {
      return firstPart.slice(0, 3).toUpperCase();
    }

    return token.slice(0, 3).toUpperCase();
  }

  private getLovLabel(option: any): string | null {
    if (!option) {
      return null;
    }
    if (typeof option === 'string') {
      return option;
    }
    return option?.Value ?? option?.value ?? option?.Name ?? option?.label ?? null;
  }

  private getPeriodDateRange(period: string): { fromDate: string; toDate: string } {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    if (period === 'Last year') {
      const targetYear = year - 1;
      return {
        fromDate: this.formatDate(new Date(targetYear, 0, 1)),
        toDate: this.formatDate(new Date(targetYear, 11, 31))
      };
    }

    if (period === 'Current year') {
      return {
        fromDate: this.formatDate(new Date(year, 0, 1)),
        toDate: this.formatDate(new Date(year, 11, 31))
      };
    }

    if (period === 'Previous month') {
      const from = new Date(year, month - 1, 1);
      const to = new Date(year, month, 0);
      return {
        fromDate: this.formatDate(from),
        toDate: this.formatDate(to)
      };
    }

    if (period === 'Current month') {
      const from = new Date(year, month, 1);
      const to = new Date(year, month + 1, 1);
      return {
        fromDate: this.formatDate(from),
        toDate: this.formatDate(to)
      };
    }

    return { fromDate: '', toDate: '' };
  }

  private getRequestDateRange(period: string): { fromDate: string; toDate: string } {
    if (this.showCustomDateRange && this.selectedDateFrom) {
      return {
        fromDate: this.formatDate(this.selectedDateFrom),
        toDate: this.formatDate(this.selectedDateTo ?? this.today)
      };
    }
    return this.getPeriodDateRange(period);
  }

  private formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private formatDateDisplay(date: Date | null): string {
    if (!date) {
      return '';
    }
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  private formatCompactNumber(value: number): string {
    if (!Number.isFinite(value)) {
      return '0';
    }

    if (value >= 1000) {
      const compact = value / 1000;
      const decimals = compact >= 100 ? 0 : 1;
      return `${compact.toFixed(decimals).replace(/\.0$/, '')}K`;
    }

    return String(Math.round(value));
  }

  private getValueFromRow(row: any, keys: string[]): string {
    for (const key of keys) {
      if (row?.[key] != null) {
        return String(row[key]).trim();
      }
    }
    return '';
  }
}
